import React, {useState, useEffect, useRef} from "react";
import {Button, Spinner} from 'react-bootstrap';
import AcFilterTable from "../AdvancedCombobox/AcFilterTable";
import AcModalEditTable from "../AdvancedCombobox/AcModalEditTable";
import "./index.css";
import Checkbox from "../../common/CheckBox";
import {AiOutlineClear} from "react-icons/ai";
import {useTranslation} from "../../../hooks/useTranslation";

const AdvancedCombobox = (props) => {
    const {t} = useTranslation();
    const {
        popupClassName,
        disabled,
        allowSelection,
        data,
        columns,
        deleteEnable,
        label,
        searchTableEnable,
        showTableOptionOnSelect,
        placeholder,
        onChange,
        onDelete,
        onKeyDown,
        inputFieldClasses,
        cellName,
        onChangeSearchCell,
        onChangeAdvancedComboboxCheckbox,
        showAdvancedComboboxCheckbox,
        isPayrollCombobox,
        onFocus,

        autoLock = false, // New functionality to lock when having selected something and add X button to reset
    } = props;
    const [acData, setAcData] = useState([]);
    const [filterData, setFilterData] = useState([]);
    const [advancedComboboxCheckbox, setAdvancedComboboxCheckbox] = useState("active");
    const [acColumns, setAcColumns] = useState([]);
    const [searchKeyword, setSearchKeyword] = useState('');
    const prevSearchKeyword = useRef(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showTableOptions, setShowTableOptions] = useState(false);

    const acRef = useRef(null);
    const endRef = useRef(null);

    const [loader, setLoader] = useState(false);
    const [willDeleteWholeKeyword, setWillDeleteWholeKeyword] = useState(false);
    const [selectedRowIndex, setSelectedRowIndex] = useState(null);
    const [keyboardMode, setKeyboardMode] = useState(false);

    const rowDelete = {
        delete: (e, key, row) => {
            onDelete(e, key, row)
        },
    }

    // Filter Data
    const clearFilter = () => {
        setLoader(false);
        if (showTableOptionOnSelect === true) {
            setFilterData(data);
        } else {
            setFilterData([]);
        }
    }

    const searchHandleKeyDown = (e) => {
        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault();
            setKeyboardMode(true);

            setSelectedRowIndex((prevIndex) => {
                const totalRows = filterData.length;
                if (totalRows === 0) return null;
                if (prevIndex === null) return 0;
                if (e.key === "ArrowDown") return Math.min(prevIndex + 1, totalRows - 1);
                if (e.key === "ArrowUp") return Math.max(prevIndex - 1, 0);
                return prevIndex;
            });
            return;
        }

        if (e.key === "Enter") {
            e.preventDefault();

            if (filterData.length === 1) {
                handleSelectOption.selected(filterData[0]);
                return;
            }

            if (selectedRowIndex !== null) {
                const selectedRow = filterData[selectedRowIndex];
                if (selectedRow) {
                    handleSelectOption.selected(selectedRow);
                }
            }
            return;
        }

        if ((e.key === 'Backspace' || e.key === 'Delete') && willDeleteWholeKeyword) {
            if (props?.resetFilter) props.resetFilter();
            prevSearchKeyword.current = searchKeyword;
            setSearchKeyword('');
            clearFilter();
            setAdvancedComboboxCheckbox("active");
        } else {
            if (willDeleteWholeKeyword) setWillDeleteWholeKeyword(false);
        }

        if (e.key === "Tab") {
            setShowTableOptions(false);
        }

        onKeyDown && onKeyDown(e);
    }

    const onChangeSearchKeyword = (e) => {
        if (!showTableOptions) setShowTableOptions(true);

        let value = String(e.target.value);
        prevSearchKeyword.current = searchKeyword;
        setSearchKeyword(value);
        if (value && value.length > 0) {
            if (props?.onChangeSearch) {
                props.onChangeSearch(value, setFilterData)
            }
            if (onChangeSearchCell && onChangeAdvancedComboboxCheckbox) {
                onChangeSearchCell(cellName, value, setFilterData, advancedComboboxCheckbox === "inactive" ? "active" : "inactive")
            } else if (onChangeSearchCell) {
                onChangeSearchCell(cellName, value, setFilterData)
            }
            setLoader(false);
        } else {
            if (props.resetFilter !== null && props.resetFilter !== undefined) {
                props.resetFilter()
            }
            clearFilter();
        }
    }

    const handleSearchOnClick = (e) => {
        if (showTableOptionOnSelect === true) {
            if (!onChangeAdvancedComboboxCheckbox && !showTableOptions) {
                setFilterData(data);
                setAdvancedComboboxCheckbox("active");
            }
            if (showAdvancedComboboxCheckbox && e.target?.value) {
                onChangeSearchCell(cellName, e.target.value, setFilterData, advancedComboboxCheckbox === "inactive" ? "active" : "inactive")
            }
        }
        if (showTableOptions) {
            setShowTableOptions(true);
        } else {
            setShowTableOptions(true);
        }
    };

    const handleClickOutside = (event) => {
        // It will check if clicked inside the advanced combobox
        if (
            acRef.current && !acRef.current.contains(event.target) &&
            event.target.textContent !== 'OK' &&
            event.target.textContent !== 'Cancel' && (event.target?.name !== "advancedComboboxCheckbox" && event.target?.className !== "checkmark")
        ) {
            setShowTableOptions(false);
        }
    };

    const handleFocus = (e) => {
        setWillDeleteWholeKeyword(true);
        if (onFocus) onFocus(e);
    }

    useEffect(() => {
        if (showTableOptions && endRef.current) {
            const scrollTimeout = setTimeout(() => {
                if (endRef.current) {
                    const rect = endRef.current.getBoundingClientRect();
                    const isBelowViewport = rect.bottom > window.innerHeight;
                    if (isBelowViewport) {
                        endRef.current.scrollIntoView({ behavior: "smooth", block: "end", inline: "nearest" });
                    }
                }
            }, 100);

            return () => clearTimeout(scrollTimeout);
        }
    }, [showTableOptions, filterData]);

    useEffect(() => {
        setAcData(data);
        if (deleteEnable) {
            const actionObj = {
                label: t('ClientsNewGeneral.contactPoints.actions'),
                name: "actions",
            }
            setAcColumns([...columns, actionObj]);
        } else {
            setAcColumns(columns);
        }

        // Fill input with value on selection
        if (showTableOptionOnSelect && props.defaultValue) {
            setSearchKeyword(props.defaultValue);
            prevSearchKeyword.current = searchKeyword;
        }

        // Show and hide outside click logic
        document.addEventListener('click', handleClickOutside, true);

        // Set Filter to data
        setFilterData(data);

        return () => {
            clearStates()
            document.removeEventListener('click', handleClickOutside, true);
        }
    }, [columns, data, deleteEnable, props])

    useEffect(() => {
        setFilterData(data);
    }, [props])

    const clearStates = () => {
        setAcColumns([]);
        setAcData([]);
    }

    const handleSelectOption = {
        selected: (row) => {
            props.field && setSearchKeyword(row[props.field]); // props.field only available for editable table fields
            setShowTableOptions(false); // on select hide popup
            onChange && onChange({value: row[props.field], row: row})
        },
    }

    const formatPayrollCombobox = (val) => String(val).replace(/\|/g, " ");

    return (
        <>
            <div className={`advanced-combobox ${props?.className}`}>
                {/* Label */}
                {label && <label>{label} {props.required && <span className="text-danger">*</span>}</label>}

                {/* Search Field */}
                <input
                    ref={acRef}
                    placeholder={placeholder && !disabled ? placeholder : ""}
                    value={isPayrollCombobox ? formatPayrollCombobox(searchKeyword) : searchKeyword}
                    onKeyDown={(e) => searchHandleKeyDown(e)}
                    onChange={(e) => onChangeSearchKeyword(e)}
                    className={inputFieldClasses ? inputFieldClasses : "form-control"}
                    type={"text"}
                    name={"search"}
                    disabled={disabled || (autoLock && props.defaultValue)}
                    autoComplete={"off"}
                    onClick={(e) => handleSearchOnClick(e)}
                    onFocus={(e) => handleFocus(e)}
                />

                {(autoLock && props.defaultValue) && (
                    <Button style={{position: "absolute", bottom: "17px", right: "15px", padding: "5px"}}
                            title={"Εκκαθάριση πεδίου"}
                            size={"sm"} onClick={() => props.resetFilter()}><AiOutlineClear size={20} /></Button>
                )}

                {searchTableEnable && (
                    <div className="ac-result">
                        {loader && (<div className="text-center py-1 bg-white"><Spinner animation="grow"/></div>)}
                        {!loader && (searchKeyword.length > 2) && (
                            <AcFilterTable prevSearchKeyword={prevSearchKeyword} tableColumns={acColumns}
                                           tableData={filterData} {...props} rowActions={rowDelete}
                                           searchKeyword={searchKeyword}/>)}
                    </div>
                )}

                {!loader && showEditModal && (
                    <AcModalEditTable
                        {...props}
                        acData={acData}
                        tableColumns={acColumns}
                        tableData={filterData}
                        closeModal={() => setShowEditModal(false)}
                        rowActions={rowDelete}
                    />
                )}

                {showTableOptionOnSelect && showTableOptions && allowSelection !== false &&
                    <div ref={acRef} className={"popupOptions " + (popupClassName ?? "")}>
                        {(onChangeAdvancedComboboxCheckbox && showAdvancedComboboxCheckbox) && (
                            <div className="d-flex align-items-center justify-content-end" style={{borderBottom: "1px solid grey"}}>
                                Εμφάνιση Όλων
                                <Checkbox
                                    className="ml-2 mb-2"
                                    name="advancedComboboxCheckbox"
                                    defaultValue={advancedComboboxCheckbox === "active" ? "inactive" : "active"}
                                    value={advancedComboboxCheckbox}
                                    onChange={(e) => {
                                        onChangeAdvancedComboboxCheckbox(e.target.checked ? "active" : "inactive", setAdvancedComboboxCheckbox, cellName, setFilterData, searchKeyword);
                                    }}
                                />
                                <hr/>
                            </div>
                        )}
                        {loader && (<div className="text-center py-1 bg-white"><Spinner animation="grow"/></div>)}
                        <AcFilterTable
                            {...props}
                            tableColumns={acColumns}
                            tableData={filterData}
                            rowActions={rowDelete}
                            selectOption={handleSelectOption}
                            searchKeyword={searchKeyword}
                            prevSearchKeyword={prevSearchKeyword}
                            selectedRowIndex={selectedRowIndex}
                            suppressHover={keyboardMode}
                            onRowHover={(i) => {
                                if (!keyboardMode) { // only update if not in keyboard mode
                                    setSelectedRowIndex(i);
                                }
                            }}
                            onMouseMove={() => setKeyboardMode(false)} // re-enable hover if mouse moves
                        />
                        <div style={{borderBottom: '6px solid #aed1d7'}} ref={endRef} className="scrollAdvancedCombobox"></div>
                    </div>
                }
            </div>
        </>
    )
}
export default AdvancedCombobox;
